import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { api } from '@/api/client'
import { EntityCrud } from '@/components/entity-crud'

import { fetchUnits, unitsKey, type MeasurementUnit } from './api'

const schema = z.object({
  name: z.string().trim().min(1, 'Give it a name').max(64),
  unit: z.string().max(16),
  is_integer: z.boolean(),
  use_si_prefix: z.boolean(),
})
type Values = z.infer<typeof schema>

export function UnitsScreen() {
  const query = useQuery({ queryKey: unitsKey, queryFn: fetchUnits })

  return (
    <EntityCrud<MeasurementUnit, Values>
      entity="units"
      singular="unit"
      rows={query.data}
      isPending={query.isPending}
      error={query.error}
      queryKey={unitsKey}
      keyOf={(row) => row.id}
      labelOf={(row) => row.name}
      columns={[
        { header: 'Name', cell: (row) => <span className="font-medium">{row.name}</span> },
        {
          header: 'Symbol',
          cell: (row) => row.unit || <span className="text-muted-foreground">none</span>,
        },
        {
          header: 'Counted in',
          cell: (row) => (row.is_integer ? 'whole units' : 'fractions allowed'),
        },
        {
          header: 'SI prefixes',
          cell: (row) => (row.use_si_prefix ? 'yes' : 'no'),
        },
      ]}
      fields={[
        { name: 'name', label: 'Name', placeholder: 'Metre' },
        { name: 'unit', label: 'Symbol', placeholder: 'm' },
        {
          name: 'is_integer',
          label: 'Whole units only',
          type: 'checkbox',
          description: 'Pieces are counted whole; wire is not.',
        },
        { name: 'use_si_prefix', label: 'Use SI prefixes', type: 'checkbox' },
      ]}
      schema={schema}
      emptyValues={{ name: '', unit: '', is_integer: true, use_si_prefix: false }}
      toValues={(row) => ({
        name: row.name,
        unit: row.unit,
        is_integer: row.is_integer,
        use_si_prefix: row.use_si_prefix,
      })}
      onCreate={(values) => api.POST('/api/units', { body: values })}
      onUpdate={(row, values) =>
        api.PUT('/api/units/{id}', { params: { path: { id: row.id } }, body: values })
      }
      onDelete={(row) => api.DELETE('/api/units/{id}', { params: { path: { id: row.id } } })}
    />
  )
}
